import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, Mail, Instagram, Facebook } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { FloatingPlus } from "./Decor";
import { MeshBlobsAmbience } from "./Ambience";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const projectTypes = [
  "Lancement Express",
  "Site Vitrine Complet",
  "Projet Sur-Mesure",
  "Refonte de site existant",
  "Je ne sais pas encore",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Merci de remplir nom, email et message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Message envoyé. À très vite !");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Réessayez ou écrivez-nous directement.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden py-28 sm:py-40 px-5 sm:px-8 lg:px-12 border-t border-white/[0.06]"
    >
      <MeshBlobsAmbience />
      <FloatingPlus x="4%" y="10%" size={20} delay={0.3} accent />
      <FloatingPlus x="94%" y="85%" size={16} delay={0.7} />

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* LEFT — Headline + meta */}
        <div className="lg:col-span-5">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6 block">
            (08) — Contact
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tighter mb-8"
          >
            Prêt à avoir un site qui ressemble enfin à <span className="text-outline">votre commerce</span> ?
          </motion.h2>
          <p className="text-zinc-400 leading-relaxed mb-10 max-w-md">
            Réservez un audit gratuit de 30 minutes, ou envoyez-nous un message. Nous analysons votre situation, identifions vos opportunités et vous proposons la meilleure solution.
          </p>

          <ul className="space-y-4 mb-10">
            <li className="flex items-center gap-3 text-zinc-300">
              <Mail className="w-4 h-4 text-[var(--accent)]" />
              <a href="mailto:ml.agence04@gmail.com" data-testid="contact-email-link">
                ml.agence04@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-3 text-zinc-300">
              <Instagram className="w-4 h-4 text-[var(--accent)]" />
              <a
                href="https://www.instagram.com/ml._agence/"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram-link"
              >
                @ml._agence
              </a>
            </li>
            <li className="flex items-center gap-3 text-zinc-300">
              <Facebook className="w-4 h-4 text-[var(--accent)]" />
              <a
                href="https://www.facebook.com/"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-facebook-link"
              >
                ML. Agence
              </a>
            </li>
          </ul>

          <ul className="space-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            <li>✓ Audit gratuit, sans engagement</li>
            <li>✓ Réponse personnalisée sous 24-48h</li>
            <li>✓ Devis clair et transparent</li>
          </ul>
        </div>

        {/* RIGHT — Tabs */}
        <div className="lg:col-span-7">
          <Tabs defaultValue="calendly" className="w-full">
            <TabsList
              className="grid grid-cols-2 w-full bg-transparent border border-white/[0.08] rounded-full p-1 h-auto mb-8"
              data-testid="contact-tabs"
            >
              <TabsTrigger
                value="calendly"
                data-testid="tab-calendly"
                className="rounded-full font-mono text-[11px] uppercase tracking-[0.2em] py-3 data-[state=active]:bg-[var(--accent)] data-[state=active]:text-black text-zinc-400"
              >
                <Calendar className="w-3.5 h-3.5 mr-2" />
                Réserver
              </TabsTrigger>
              <TabsTrigger
                value="email"
                data-testid="tab-email"
                className="rounded-full font-mono text-[11px] uppercase tracking-[0.2em] py-3 data-[state=active]:bg-[var(--accent)] data-[state=active]:text-black text-zinc-400"
              >
                <Mail className="w-3.5 h-3.5 mr-2" />
                Écrire un message
              </TabsTrigger>
            </TabsList>

            <TabsContent value="calendly" className="mt-0">
              <div
                data-testid="calendly-embed"
                className="rounded-2xl overflow-hidden border border-white/[0.08] bg-[var(--bg-soft)]"
                style={{ height: 720 }}
              >
                <iframe
                  src="https://calendly.com/ml-agence04/30min?hide_landing_page_details=1&hide_gdpr_banner=1&background_color=0a0a0a&text_color=f4f4f5&primary_color=e0ff22"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="Réserver un audit gratuit — Calendly"
                />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-4">
                Audit de 30 min • Visio ou téléphone • 100% gratuit
              </p>
            </TabsContent>

            <TabsContent value="email" className="mt-0">
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bento p-7 sm:p-9 space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Nom *
                    </label>
                    <input
                      data-testid="contact-input-name"
                      className="field"
                      placeholder="Marie Dupont"
                      value={form.name}
                      onChange={update("name")}
                      required
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Email *
                    </label>
                    <input
                      data-testid="contact-input-email"
                      type="email"
                      className="field"
                      placeholder="vous@example.com"
                      value={form.email}
                      onChange={update("email")}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Téléphone
                    </label>
                    <input
                      data-testid="contact-input-phone"
                      className="field"
                      placeholder="06 00 00 00 00"
                      value={form.phone}
                      onChange={update("phone")}
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Type de projet
                    </label>
                    <select
                      data-testid="contact-input-project-type"
                      className="field bg-transparent"
                      value={form.project_type}
                      onChange={update("project_type")}
                    >
                      <option value="" className="bg-black">— Sélectionnez —</option>
                      {projectTypes.map((t) => (
                        <option key={t} value={t} className="bg-black">
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Votre message *
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    className="field"
                    rows={5}
                    placeholder="Parlez-nous de votre projet, vos objectifs, votre échéance…"
                    value={form.message}
                    onChange={update("message")}
                    required
                  />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Réponse sous 24-48h
                  </span>
                  <button
                    type="submit"
                    data-testid="contact-form-submit"
                    disabled={submitting}
                    className="btn-pill btn-pill-accent disabled:opacity-60"
                  >
                    {submitting ? "Envoi en cours…" : "Envoyer le message"}
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}
